<template>
  <div v-if="video" :class="getVideoInfoClass">
    <b-row align-v="center" v-if="video">
      <b-col>
        <router-link :to="{name: 'artist-about', params: { artistId: this.artist.id }}"><img class="artist-avatar" :src="artist.avatar_image"></router-link>
        <h4>
          <router-link :to="{name: 'video-detail', params: {id: video.id}}" class="title-link">
            {{ video.title }}
          </router-link>
        </h4>
        <small>
          By
          <router-link :to="{name: 'artist-about', params: { artistId: this.artist.id }}">{{ artist.name }}</router-link>
          {{ videoTime }}
          <b-button class="ellipsis" @click="showDetails = !showDetails">
            <font-awesome-icon icon="ellipsis-h"></font-awesome-icon>
          </b-button>
        </small>
      </b-col>
      <b-col cols="12" sm="6" class="text-right video-info-stats">
        <span><font-awesome-icon icon="eye" /> {{ video.view_count }}</span>
        <span :class="likeClass"><font-awesome-icon icon="heart" @click="like"/> {{ video.rate_count }}</span>
        <span v-if="!hideComments"><font-awesome-icon icon="comment" /> {{ video.comment_count }}</span>

        <div class="actions" v-if="isCurrentArtist">
          <LinkButton :id="'btnShareVideo' + video.id">
            <font-awesome-icon icon="share" /><span class="share-label">Share</span>
          </LinkButton>
          <Share :target="'btnShareVideo' + video.id" :text="video.title" :shareUrl="video.get_absolute_url" :openGraph="video.opengraph_metadata" />
        </div>

        <div class="actions" v-else>
          <LinkButton :id="'btnShareVideo' + video.id" v-if="isLoggedIn">
            <font-awesome-icon icon="share" /><span class="share-label">Share</span>
          </LinkButton>
          <Share v-if="isLoggedIn"
                 :target="'btnShareVideo' + video.id"
                 :text="video.title"
                 :shareUrl="video.get_absolute_url"
                 :openGraph="video.opengraph_metadata"
          />
          <router-link class="white-link" :to="{ name: 'account-login', query: { next: $route.fullPath }}" v-if="!isLoggedIn">
            <font-awesome-icon icon="share" />Share
          </router-link>

          <Pay :video="video" />
        </div>
      </b-col>
    </b-row>

    <b-row class="pt-4" v-if="showDetails && this.video">
      <b-col v-html="formatText(this.video.lyrics)" />
      <b-col cols="12" lg="4" class="mt-4" v-if="contributors.length">
        <h5><strong>Contributors</strong></h5>
        <UserList v-model="contributors" :withRoles="true" class="vertical" />
      </b-col>
    </b-row>

    <Comments class="pt-4" :video="video" :collapsed="!expandComments" v-if="!hideComments" />
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .ellipsis {
    padding: 0 5px;
    line-height: 0.5em;
    border-radius: 0;
    margin-top: 0.5em;
    margin-bottom: 5px;
    margin-left: 1em;
    background: $darkish-pink;
    border: $dark-pink;
    font-size: 0.8em;
  }

  .video-info {
    /* margin-top: -0.5em; */
    margin-bottom: 1em;

    &.bottom-border {
      padding-bottom: 1em;
      border-bottom: 1px solid $warm-grey;
      padding-top: 1em;
    }

    img.artist-avatar {
      float: left;
      width: 48px;
      height: 48px;
      border-radius: 100%;
      margin-right: 1em;
    }

    h4 {
      margin-bottom: 0;
      @include media-breakpoint-down(xs) {
        font-size: 1rem;
      }

      .title-link {
        color: $white;
      }
    }

    .white-link {
      color: $white;
    }

    small {
      color: $steel;
    }

    span {
      margin-left: 1em;
    }

    .share-label {
      margin-left: .5em;
      @include media-breakpoint-down(sm) {
        display: none !important;
      }
    }

    .like {
      &:hover {
        color: $darkish-pink;
      }

      &.disabled:hover {
        color: white;
      }

      &.already-liked {
        color: $darkish-pink;
      }
    }

    .actions {
      display: flex;
      width: 100%;
      flex-direction: row;
      justify-content: flex-end;
      margin-top: 0.5em;
      align-items: center;
    }

    .video-info-stats {
      @include media-breakpoint-down(xs) {
        display: flex;
        align-items: center;
        padding: 0 1.5em;
        margin-top: 1em;
        .actions {
          margin-top: 0;
        }
        span {
          margin-left: 0;
          margin-right: 1em;
          display: flex;
          align-items: center;
          font-size: 0.9rem;
          svg {
            margin-right: .5em;
            transform: scale(0.9);
          }
        }
      }
    }
  }
</style>

<script>
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import moment from '@/utils/moment'
import { mapGetters } from 'vuex'

import { formatText } from '@/utils/textformat'

import Pay from './Pay'
import Share from './Share'
import LinkButton from './LinkButton'
import Comments from './Comments'
import UserList from './UserList'

export default {
  name: 'VideoInfo',
  components: {
    FontAwesomeIcon,
    Pay,
    LinkButton,
    Share,
    Comments,
    UserList
  },
  props: {
    video: Object,
    expandComments: Boolean,
    hideComments: Boolean
  },
  data () {
    return {
      showDetails: false,
      hasAccessTo: null
    }
  },
  computed: {
    getVideoInfoClass () {
      let cls = ['video-info']
      if (!this.hideComments) cls.push('bottom-border')
      return cls
    },
    artist () {
      return this.video ? this.video.artist : {}
    },
    isCurrentArtist () {
      return this.artist.id === this.$store.getters['profile/currentUser'].artist_id
    },
    videoTime () {
      return moment.utc(new Date(this.video.created_at)).fromNow()
    },
    likeClass () {
      let cls = ['like']
      if (this.video && this.video.rated_by_user) cls.push('already-liked')
      if (!this.isLoggedIn) cls.push('disabled')
      return cls
    },
    contributors () {
      if (!this.video || !this.video.song_credits || !Object.keys(this.video.song_credits).length) return []

      let cs = []
      for (let c in this.video.song_credits) {
        cs.push({
          handle: this.video.song_credits[c],
          user: this.video.song_credits_expanded[c],
          role: c
        })
      }
      return cs
    },
    ...mapGetters('auth', ['isLoggedIn'])
  },
  methods: {
    async like () {
      if (!this.video) return

      if (this.video.rated_by_user) {
        try {
          await this.$store.dispatch('song/unlikeSong', {id: this.video.id})
        } catch (e) {
          console.error('Error unliking song', e, e.response)
          return
        }
        this.video.rated_by_user = false
        this.video.rate_count--
      } else {
        try {
          await this.$store.dispatch('song/likeSong', {id: this.video.id})
        } catch (e) {
          console.error('Error liking song', e, e.response)
        }
        this.video.rated_by_user = true
        this.video.rate_count++
      }
    },
    formatText
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/VideoInfo.vue